import React, { useState, useContext, useEffect, useMemo } from "react";
import { hasRole } from "../utils/guestUser";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { AuthContext } from "../context/AuthContext";
import { UserContext } from "../context/UserContext";
import "../Styles/dashboard8.css";
import { useNavigate } from "react-router-dom";
import { taskApiUrl } from "../utils/GetUrl";

export default function AccessibleTable() {
  const navigate = useNavigate();

  const { token, userData } = useContext(AuthContext);
  const { teamNames, usersList } = useContext(UserContext);

  const [viewForce, setViewForce] = useState({});
  const [weeklyReport, setWeeklyReport] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ---------------- WEEK PICKER ----------------
  const getCurrentWeek = () => {
    const now = new Date();
    const oneJan = new Date(now.getFullYear(), 0, 1);
    const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((now.getDay() + 1 + numberOfDays) / 7);
    return `${now.getFullYear()}-W${String(week).padStart(2, "0")}`;
  };

  const [selectedDate, setSelectedDate] = useState(getCurrentWeek());

  // ---------------- DATE RANGE ----------------
  useEffect(() => {
    if (!selectedDate) return;

    const [year, weekStr] = selectedDate.split("-W");
    const week = parseInt(weekStr);

    const getStartOfWeek = (year, week) => {
      const simple = new Date(year, 0, 1 + (week - 1) * 7);
      const dow = simple.getDay();
      const start = new Date(simple);

      if (dow <= 4) start.setDate(simple.getDate() - simple.getDay() + 1);
      else start.setDate(simple.getDate() + 8 - simple.getDay());

      return start;
    };

    const start = getStartOfWeek(year, week);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    const fmt = (d) => d.toISOString().split("T")[0];

    setStartDate(fmt(start));
    setEndDate(fmt(end));
  }, [selectedDate]);

  // ---------------- HELPERS ----------------
  const arrayToString = (str) => str.replace(/'/g, "");

  const fetchViewForce = async (EMIds) => {
    if (!EMIds) return [];
    try {
      const res = await fetch(
        `${taskApiUrl}/GetDataByDate?StartDate=${startDate}&EndDate=${endDate}&EMId=${arrayToString(
          EMIds
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 401) return [];
      if (res.ok) return await res.json();
    } catch (e) {
      console.error(e);
    }
    return [];
  };

  const fetchWeekly = async (EMIds) => {
    if (!EMIds) return [];
    try {
      const res = await fetch(
        `${taskApiUrl}/GetAllTaskClientWiseReport?DateRange1=${startDate}&DateRange2=${endDate}&AssginedToEmids=${EMIds}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) return await res.json();
    } catch (e) {
      console.error(e);
    }
    return [];
  };

  // ---------------- FASTEST API CALLS ----------------
  useEffect(() => {
    if (!teamNames || !usersList || !startDate) return;

    // Run team fetches in small batches instead of all at once, so we don't
    // flood the task function app (which responds with 429 / dropped CORS
    // headers under burst load). Results are keyed by teamId below, so batching
    // changes only the request pacing — not the data or the rendered output.
    const runInBatches = async (items, batchSize, worker) => {
      const out = [];
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        // eslint-disable-next-line no-await-in-loop
        out.push(...(await Promise.all(batch.map(worker))));
      }
      return out;
    };

    const fetchAllTeams = async () => {
      const activeTeams = teamNames.filter(
        (t) => t?.status === true && t?.type === "T"
      );

      const results = await runInBatches(activeTeams, 3, async (team) => {
        const teamEmployees = usersList
          .filter((u) => u?.STM_Team == team.id)
          .map((u) => `'${u.EM_ID}'`)
          .join(",");

        const [vfData, weeklyData] = await Promise.all([
          fetchViewForce(teamEmployees),
          fetchWeekly(teamEmployees),
        ]);

        return { teamId: team.id, vfData, weeklyData };
      });

      const vfObj = {};
      const weeklyObj = {};

      results.forEach(({ teamId, vfData, weeklyData }) => {
        vfObj[teamId] = vfData;
        weeklyObj[teamId] = weeklyData;
      });

      setViewForce(vfObj);
      setWeeklyReport(weeklyObj);
    };

    fetchAllTeams();
  }, [teamNames, usersList, startDate, endDate]);

  // ---------------- ROWS (UI SAFE) ----------------
  const rows = useMemo(() => {
    return teamNames
      ?.filter(
        (team) =>
          team?.status === true &&
          team?.type === "T" &&
          !(
            hasRole(userData?.[0]?.DR_Name, "Consultant") &&
            team?.id != userData?.[0]?.STM_Team
          )
      )
      ?.map((team) => {
        const teamViewForce = viewForce[team.id] || [];
        const teamWeeklyReport = weeklyReport[team.id] || [];

        const vfTime = teamViewForce.reduce(
          (t, v) => t + (parseFloat(v.registeredTime) || 0),
          0
        );

        const actualTime = teamWeeklyReport.reduce(
          (t, w) => t + (parseFloat(w.TotalTime) || 0),
          0
        );

        return {
          name: team.name,
          vft: vfTime,
          at: actualTime,
          d: actualTime - vfTime,
        };
      });
  }, [teamNames, viewForce, weeklyReport, userData]);

  // ---------------- UI ----------------
  return (
    <div className="lastTable-dashboard">
      <div className="last-dash-head">
        <div
          className="last-dash-heading1"
          onClick={() => navigate("/WeeklyReview")}
          style={{ cursor: "pointer" }}
        >
          WEEKLY TIME REVIEW
        </div>

        <div className="last-dash-drop">
          <input
            type="week"
            className="date-design-weekly"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <div className="last-dash-subhead">
        <div>Budgeted and actual value comparison</div>
      </div>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: 302, overflow: "auto" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="caption table">
          <TableHead>
            <TableRow
              className="t-r-head-lg"
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 1,
              }}
            >
              <TableCell>Team Name</TableCell>
              <TableCell align="right">REPORTED TIME</TableCell>
              <TableCell align="right">ACTUAL TIME</TableCell>
              <TableCell align="right">DIFFERENCE</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows?.map((row, index) => (
              <TableRow
                key={row.name}
                className={index % 2 === 0 ? "odd-row" : "even-row"}
              >
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell align="right">{(row.vft / 60).toFixed(2)}</TableCell>
                <TableCell align="right">{(row.at / 60).toFixed(2)}</TableCell>
                <TableCell
                  align="right"
                  className={index % 2 === 0 ? "odd-cell" : "even-cell"}
                >
                  {(row.d / 60).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
